import { useAuthStore } from '~/store/auth';

const STORAGE_KEY = 'report_shop_uid';

const isValidShopUid = (value: unknown): value is string => {
  return typeof value === 'string' && value.length > 0;
};

const getStoredShopUid = (currentOrgId?: number): string | null => {
  if (import.meta.client) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (isValidShopUid(parsed.shop_uid)) {
          if (parsed.organization_id !== undefined) {
            if (currentOrgId !== undefined && parsed.organization_id !== currentOrgId) {
              localStorage.removeItem(STORAGE_KEY);
              return null;
            }
          }
          return parsed.shop_uid;
        }
      }
    } catch (error) {
      console.warn('Failed to parse stored shopUid:', error);
    }
  }
  return null;
};

/**
 * The shop that a report page shows: the `shop_uid` query, then the stored report shop, then the
 * current shop. `plugins/gtag-pageview.client.ts` also calls this. It runs before the page setup,
 * so it cannot read the shop ref of the page.
 */
export const resolveReportShopUid = (
  queryShopUid: unknown,
  authStore: Pick<ReturnType<typeof useAuthStore>, 'shops' | 'currentShop' | 'currentOrganization'>,
): string => {
  const isInOrganization = (shopUid: string) => authStore.shops.some(shop => shop.uid === shopUid);

  if (queryShopUid) {
    const shopUid = String(queryShopUid);
    if (isValidShopUid(shopUid) && isInOrganization(shopUid)) {
      return shopUid;
    }
  }

  const stored = getStoredShopUid(authStore.currentOrganization?.id);
  if (stored !== null && isInOrganization(stored)) {
    return stored;
  }

  return authStore.currentShop?.uid ?? '';
};

/**
 * Composable for managing report shopUid with localStorage persistence
 * This allows users to maintain their shop selection across different reports
 * Includes organization context to prevent stale shopUids when switching organizations
 */
export const useReportShopUid = () => {
  const route = useRoute();

  const authStore = useAuthStore();
  const { currentShop, currentOrganization } = storeToRefs(authStore);

  const isShopUidValidForOrganization = (shopUid: string, shops: Shop[] = authStore.shops): boolean => {
    return shops.some(shop => shop.uid === shopUid);
  };

  const saveShopUid = (shop_uid: string, organizationId?: number): void => {
    if (import.meta.client) {
      try {
        const orgId = organizationId ?? currentOrganization.value?.id;
        if (!orgId) {
          console.warn('Cannot save shopUid without organization context');
          return;
        }

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            shop_uid,
            organization_id: orgId,
            updatedAt: new Date().toISOString(),
          }),
        );
      } catch (error) {
        console.warn('Failed to save shopUid:', error);
      }
    }
  };

  const initializeShopUid = (): string => resolveReportShopUid(route.query.shop_uid, authStore);

  const createShopUid = (): Ref<string> => {
    const shopUid = ref(initializeShopUid());

    watch(shopUid, newShopUid => {
      saveShopUid(newShopUid, currentOrganization.value?.id);
    });

    watch(
      () => currentOrganization.value?.id,
      (newOrgId, oldOrgId) => {
        if (newOrgId !== oldOrgId && newOrgId !== undefined) {
          if (!isShopUidValidForOrganization(shopUid.value)) {
            shopUid.value = currentShop.value?.uid ?? '';
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      },
    );

    return shopUid;
  };

  const clearStoredShopUid = (): void => {
    if (import.meta.client) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn('Failed to clear stored shopUid:', error);
      }
    }
  };

  return {
    createShopUid,
    initializeShopUid,
    getStoredShopUid,
    saveShopUid,
    clearStoredShopUid,
  };
};
